import { useState, useCallback, useRef } from "react";
import { Volume2, Loader2, Square } from "lucide-react";
import { Button } from "@/components/ui";
import { cn } from "@/lib/utils";
import { getCopilotBffHeaders, getCopilotBffUrl } from "@/lib/copilot-api";
import { isTtsEnabled } from "@/lib/feature-flags";

type TtsButtonProps = {
  content: string;
  className?: string;
};

export function TtsButton(props: TtsButtonProps) {
  if (!isTtsEnabled()) return null;
  return <TtsButtonImpl {...props} />;
}

function TtsButtonImpl({ content, className }: TtsButtonProps) {
  const [status, setStatus] = useState<"idle" | "loading" | "playing">("idle");
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handlePlay = useCallback(async () => {
    if (status === "playing") {
      // Stop playback
      audioRef.current?.pause();
      audioRef.current = null;
      setStatus("idle");
      return;
    }

    setStatus("loading");

    try {
      const text = content.length > 500 ? content.slice(0, 500) + "..." : content;
      const response = await fetch(getCopilotBffUrl("voice/synthesize"), {
        method: "POST",
        headers: getCopilotBffHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          text,
          voice: "nova",
          format: "mp3",
          speed: 1.0,
        }),
      });

      if (!response.ok) throw new Error("TTS failed");

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);

      audio.onended = () => {
        setStatus("idle");
        URL.revokeObjectURL(url);
        audioRef.current = null;
      };

      audio.onerror = () => {
        setStatus("idle");
        URL.revokeObjectURL(url);
        audioRef.current = null;
      };

      audioRef.current = audio;
      await audio.play();
      setStatus("playing");
    } catch {
      setStatus("idle");
    }
  }, [content, status]);

  return (
    <Button
      variant="ghost"
      size="icon"
      className={cn("shrink-0", className)}
      onClick={handlePlay}
      title={status === "playing" ? "Parar áudio" : "Ouvir resposta"}
      aria-label={status === "playing" ? "Parar áudio" : "Ouvir resposta"}
    >
      {status === "loading" ? (
        <Loader2 className="w-3 h-3 motion-safe:animate-spin text-muted-foreground" />
      ) : status === "playing" ? (
        <Square className="w-3 h-3 text-emerald-400 fill-emerald-400" />
      ) : (
        <Volume2 className="w-3 h-3 text-muted-foreground hover:text-foreground" />
      )}
    </Button>
  );
}
